"use client"

import { Button } from "@/components/ui/button"
import { getPwaInstallPlatform, hasHandledPwaInstallNotice } from "@/shared/lib/pwa-install"
import { publicEnv } from "@zentjes/config"
import { Bell, BellRing, X } from "lucide-react"
import { useEffect, useMemo, useState, useTransition } from "react"

type PushPromptState = "hidden" | "ready" | "blocked" | "enabled"

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
  const rawData = atob(base64)
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
}

/**
 * @returns Prompt that requests Web Push permission and subscribes supported users.
 */
export function WebPushPrompt() {
  const [state, setState] = useState<PushPromptState>("hidden")
  const [isDismissed, setIsDismissed] = useState(false)
  const [isPending, startTransition] = useTransition()

  const vapidPublicKey = useMemo(() => publicEnv.NEXT_PUBLIC_WEB_PUSH_VAPID_KEY, [])

  useEffect(() => {
    const hasHandledInstallPrompt = hasHandledPwaInstallNotice(getPwaInstallPlatform())
    if (!hasHandledInstallPrompt) return

    const dismissed = localStorage.getItem("web-push-prompt-dismissed")
    if (dismissed === "true") {
      setIsDismissed(true)
      return
    }

    const isSupported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
    if (!isSupported || !vapidPublicKey) return

    if (Notification.permission === "granted") {
      setState("enabled")
      return
    }

    if (Notification.permission === "denied") {
      setState("blocked")
      return
    }

    setState("ready")
  }, [vapidPublicKey])

  function dismiss() {
    setIsDismissed(true)
    setState("hidden")
    localStorage.setItem("web-push-prompt-dismissed", "true")
  }

  function enablePush() {
    if (!vapidPublicKey) return

    startTransition(async () => {
      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        setState(permission === "denied" ? "blocked" : "hidden")
        return
      }

      const registration = await navigator.serviceWorker.ready
      await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      })
      setState("enabled")
    })
  }

  if (isDismissed || state === "hidden") return null

  return (
    <aside className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] left-4 right-4 z-50 border border-border bg-background/95 p-4 shadow-[0_12px_32px_-22px_rgba(90,90,90,0.5)] backdrop-blur-sm md:bottom-6 md:left-auto md:right-6 md:w-[420px]">
      <div className="grid grid-cols-[auto,1fr,auto] items-start gap-3">
        <div className="mt-0.5 flex h-10 w-10 items-center justify-center border border-border bg-muted text-foreground">
          {state === "enabled" ? <BellRing size={16} /> : <Bell size={16} />}
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">Stay ahead of upcoming charges</p>
          <p className="text-sm text-muted-foreground">
            Get notified when you are added to a split bill or when recurring payments are due.
          </p>
          {state === "blocked" ? (
            <p className="text-xs text-muted-foreground">Notifications are blocked. Re-enable them in your browser settings.</p>
          ) : null}
        </div>

        <button aria-label="Dismiss notification prompt" className="p-2 text-muted-foreground" onClick={dismiss}>
          <X size={14} />
        </button>
      </div>

      <div className="mt-3 flex items-center justify-end gap-2 border-t border-border pt-3">
        <Button variant="ghost" className="min-h-11" onClick={dismiss}>Not now</Button>
        {state === "ready" ? (
          <Button className="min-h-11" onClick={enablePush} disabled={isPending}>
            {isPending ? "Enabling..." : "Enable notifications"}
          </Button>
        ) : (
          <Button className="min-h-11" disabled>
            Enabled
          </Button>
        )}
      </div>
    </aside>
  )
}
